/* eslint-disable react/prop-types */
import { Typography, makeStyles } from "@material-ui/core";
import BadRequestDialog from "app/common/dialog/BadRequestDialog";
import ConfirmationDialog from "app/common/dialog/ConfirmationDialog";
import SnackbarActionButtons from "app/common/snackbar/SnackbarActionButtons";
import { changeSelectedScenarioAsyncAction } from "app/scenarios/duck/actions";
import {
  addScenarioTotals,
  setCannibalizationRequested,
} from "app/scenarios/duck/slices";
import athenaClient from "lib/axios/clients";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { REACT_APP_BASE_URL } from "resources/constants";

const useStyles = makeStyles((theme) => ({
  dialogRoot: {
    maxWidth: "750px",
  },
  dialogTitle: {
    backgroundColor: theme.palette.primary.main,
    color: "#ffffff",
    padding: "10px 20px",
  },
  dialogContent: {
    padding: "35px",
  },
  textDialog: {
    marginBottom: "10px",
  },
  containerTitle: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
  },
  iconColor: {
    color: "white",
  },
  buttonsContainer: {
    display: "flex",
    justifyContent: "end",
    gap: "8px",
  },
  spinnerParent: {
    "&>span>span": {
      display: "flex",
    },
  },
  disabledButton: {
    "&:disabled": {
      backgroundColor: "#E8BDBB",
      color: "white",
    },
  },
}));

function CannibalizationDialogComponent({ openDialog, setOpenDialog }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const scenariosSlice = useSelector((state) => state.scenarios);
  const {
    selectedScenario,
    referenceScenario,
    totalsByScenarioId,
    cannibalizationRequested,
  } = scenariosSlice;
  const {
    scenario: { is_blocked: isBlocked },
  } = selectedScenario;
  const {
    scenario: { is_blocked: isReferenceBlocked },
  } = referenceScenario;

  const [error, setError] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const enqueueSuccessSnackbar = (onAction) => {
    enqueueSnackbar(
      `${
        isBlocked
          ? "Scenario cannibalization successful"
          : "Remove scenario cannibalization succesful"
      } `,
      {
        variant: "success",
        persist: "true",
        action: (key) => (
          <SnackbarActionButtons
            action="Ok"
            noDismiss="true"
            onAction={onAction}
            key={key}
          />
        ),
      }
    );
  };

  const blockedScenarioRequest = async () => {
    try {
      // Blocking or unblocking the scenario
      const blockedScenarioConfig = {
        method: "PUT",
        url: `${REACT_APP_BASE_URL}/scenarios/${selectedScenario.scenario_id}`,
        data: { id: selectedScenario.scenario_id, is_blocked: !isBlocked },
      };
      const resp = await athenaClient(blockedScenarioConfig);
      // Error handling
      if (resp.status !== 200)
        throw Error(`couldn't block scenario ${selectedScenario.scenario_id}`);
      else return resp.data;
    } catch (e) {
      setError(e);
    }
    return {};
  };

  const handleConfirm = async () => {
    dispatch(setCannibalizationRequested("loading"));
    const {
      is_blocked: isScenarioBlocked,
      id: scenarioId,
    } = await blockedScenarioRequest();

    const obj = {
      ...totalsByScenarioId,
      [scenarioId]: {
        ...totalsByScenarioId[scenarioId],
        scenario: {
          ...totalsByScenarioId[scenarioId].scenario,
          is_blocked: isScenarioBlocked,
        },
      },
    };

    // Modify the blocked status of this scenario to the state totalsByScenarioId
    dispatch(addScenarioTotals({ ...obj }));
    dispatch(changeSelectedScenarioAsyncAction(scenarioId));
    setOpenDialog(!openDialog);
  };

  const handleCancel = () => {
    setOpenDialog(!openDialog);
  };

  const onConfirmAction = (e, action) => {
    if (action) handleConfirm();
    handleCancel();
  };

  const getDialogContent = () => {
    if (isBlocked) {
      return (
        <Typography className={classes.textDialog}>
          <span>
            You will be able to make scenario modifications but until you click
            the switch again the cannibalizations will not be applied.
          </span>
          <br />
          <br />
          {isReferenceBlocked &&
            selectedScenario.scenario_id !== referenceScenario.scenario_id && (
              <span>
                The reference scenario is cannibalized. Please note the KPIs
                comparison may contain drastic values.
              </span>
            )}
        </Typography>
      );
    }
    return (
      <Typography className={classes.textDialog}>
        <span>
          You will not be able to make any new modifications until you click the
          cannibalization switch again.
        </span>
        <br />
        <br />
        {!isReferenceBlocked &&
          selectedScenario.scenario_id !== referenceScenario.scenario_id && (
            <span>
              Please note the reference scenario is not cannibalized. You may
              experience a high change within the KPIs comparison.
            </span>
          )}
      </Typography>
    );
  };

  useEffect(() => {
    if (cannibalizationRequested === "finished") {
      enqueueSuccessSnackbar(() => {});
      dispatch(setCannibalizationRequested("idle"));
    }
  }, [cannibalizationRequested]);

  return (
    <>
      <ConfirmationDialog
        title={
          isBlocked
            ? "Confirm remove scenario cannibalization"
            : "Confirm scenario cannibalization"
        }
        content={
          <div>
            <Typography
              className={classes.textDialog}
              style={{ fontWeight: "600" }}
            >
              {isBlocked
                ? "Are you sure you want to remove the cannibalization from this scenario?"
                : "Are you sure you want to cannibalize this scenario?"}
            </Typography>
            {getDialogContent()}
          </div>
        }
        open={openDialog}
        onConfirmAction={onConfirmAction}
      />
      <BadRequestDialog open={error} />
    </>
  );
}

export default CannibalizationDialogComponent;
